/**
 * Single-file payments module — 4 pages share helpers and types so we stay DRY.
 * Exports:
 *   - PaymentCheckoutPage  → /payments/checkout?sku=<sku>&target_id=<id>
 *   - MyPaymentsPage       → /payments  (user history + active pending)
 *   - AdminPaymentQueuePage → /admin/payments  (admin queue with assignment)
 *   - PaymentAdminConfigPage → /admin/payment-config  (super admin only)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  Landmark, Smartphone, DollarSign, Copy, Check, Upload, ArrowLeft, Clock,
  AlertTriangle, CheckCircle2, XCircle, Loader2, ShieldCheck, Banknote,
  TrendingUp, Wallet, Users, Settings, Save, Eye, X, RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (cents) => `USD $${(cents / 100).toFixed(2)}`;
const fmtVES = (cents) => `Bs ${(cents / 100).toLocaleString('es-VE', { maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  pending:   { bg: 'bg-amber-50',    txt: 'text-amber-700',   ring: 'ring-amber-200',   label: { es: 'Pendiente',  en: 'Pending' } },
  submitted: { bg: 'bg-blue-50',     txt: 'text-blue-700',    ring: 'ring-blue-200',    label: { es: 'En revisión', en: 'Under review' } },
  approved:  { bg: 'bg-emerald-50',  txt: 'text-emerald-700', ring: 'ring-emerald-200', label: { es: 'Aprobado',   en: 'Approved' } },
  rejected:  { bg: 'bg-red-50',      txt: 'text-red-700',     ring: 'ring-red-200',     label: { es: 'Rechazado',  en: 'Rejected' } },
  expired:   { bg: 'bg-slate-50',    txt: 'text-slate-500',   ring: 'ring-slate-200',   label: { es: 'Expirado',   en: 'Expired' } },
  cancelled: { bg: 'bg-slate-50',    txt: 'text-slate-500',   ring: 'ring-slate-200',   label: { es: 'Cancelado',  en: 'Cancelled' } },
};

function StatusPill({ status, lang = 'es' }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${s.bg} ${s.txt} ${s.ring}`} data-testid={`payment-status-${status}`}>
      {s.label[lang] || s.label.es}
    </span>
  );
}

function CopyField({ label, value, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5">
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
        <div className="font-mono text-sm text-slate-800 truncate">{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={copy} data-testid={testId} className="shrink-0 ml-2">
        {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
      </Button>
    </div>
  );
}

// ============================================================
// 1) CHECKOUT PAGE  (/payments/checkout?sku=<sku>&target_id=<id>)
// ============================================================
export function PaymentCheckoutPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sku = searchParams.get('sku') || 'agent_verification';
  const targetId = searchParams.get('target_id') || '';
  const tt = (en, es) => (language === 'es' ? es : en);
  const [step, setStep] = useState(1); // 1=method, 2=instructions, 3=proof, 4=done
  const [config, setConfig] = useState(null);
  const [method, setMethod] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [intent, setIntent] = useState(null);
  const [proofFile, setProofFile] = useState(null);
  const [userNotes, setUserNotes] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/config/public`).then(r => setConfig(r.data)).catch(() => {});
  }, []);

  if (!config) {
    return <div className="container mx-auto max-w-2xl py-10 px-4 text-center" data-testid="checkout-loading"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#D4AF37]" /></div>;
  }
  const skuDef = config.skus[sku];
  if (!skuDef) {
    return (
      <div className="container mx-auto max-w-2xl py-10 px-4 text-center" data-testid="checkout-sku-unknown">
        <p className="text-red-600">{tt('Unknown plan', 'Plan desconocido')}</p>
        <Link to="/" className="text-[#D4AF37] underline mt-4 block">{tt('Back home', 'Volver al inicio')}</Link>
      </div>
    );
  }

  const handleCreateIntent = async (chosenMethod) => {
    const ccy = chosenMethod === 'pago_movil' ? 'VES' : 'USD';
    setMethod(chosenMethod);
    setCurrency(ccy);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/intent`,
        { sku, method: chosenMethod, currency: ccy, target_id: targetId || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setIntent(data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Could not create payment', 'No se pudo crear el pago'));
    } finally { setLoading(false); }
  };

  const uploadProof = async () => {
    if (!proofFile) { toast.error(tt('Please pick a file', 'Selecciona un archivo')); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', proofFile);
      form.append('user_notes', userNotes);
      await axios.post(`${API}/payments/${intent.id}/proof`, form, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      setStep(4);
      toast.success(tt('Proof submitted!', '¡Comprobante enviado!'));
    } catch (e) {
      toast.error(e.response?.data?.detail || tt('Upload failed', 'Carga falló'));
    } finally { setLoading(false); }
  };

  return (
    <div className="container mx-auto max-w-3xl py-6 px-4 space-y-5" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-[#0B192C] flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" />{tt('Back', 'Volver')}
      </button>

      {/* Header / SKU summary */}
      <Card className="border-[#D4AF37]/40 border-2">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl text-[#0B192C]" data-testid="checkout-sku-name">
                {language === 'es' ? skuDef.name_es : skuDef.name_en}
              </h1>
              <p className="text-sm text-slate-600 mt-1">{language === 'es' ? skuDef.description_es : skuDef.description_en}</p>
            </div>
            <div className="text-right shrink-0">
              <div className="font-heading text-3xl font-bold text-[#0B192C]" data-testid="checkout-price-usd">{fmtUSD(skuDef.price_usd_cents)}</div>
              <div className="text-xs text-slate-500 mt-0.5">≈ {fmtVES(skuDef.price_ves_cents)}</div>
              {skuDef.kind === 'subscription' && (
                <Badge className="bg-amber-100 text-amber-700 mt-2">{tt(`${skuDef.duration_days} days`, `${skuDef.duration_days} días`)}</Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 1 — Method picker */}
      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="checkout-methods">
          {[
            { id: 'bank_transfer', icon: Landmark, label: tt('Bank Transfer', 'Transferencia'), sub: 'USD / VES', available: config.bank_accounts.length > 0 },
            { id: 'pago_movil',    icon: Smartphone, label: 'Pago Móvil', sub: 'VES', available: !!config.pago_movil },
            { id: 'zelle',         icon: DollarSign, label: 'Zelle', sub: 'USD', available: !!config.zelle },
          ].filter(m => m.available).map(m => (
            <button key={m.id}
              onClick={() => handleCreateIntent(m.id)}
              disabled={loading}
              className="bg-white border-2 border-slate-200 rounded-xl p-5 hover:border-[#D4AF37] hover:shadow-md transition group text-left"
              data-testid={`checkout-method-${m.id}`}>
              <m.icon className="w-8 h-8 text-[#D4AF37] mb-3" />
              <div className="font-semibold text-[#0B192C]">{m.label}</div>
              <div className="text-xs text-slate-500 mt-1">{m.sub}</div>
            </button>
          ))}
        </div>
      )}

      {/* Step 2 — Instructions */}
      {step === 2 && intent && (
        <Card data-testid="checkout-instructions">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg text-[#0B192C]">{tt('Send your payment', 'Envía tu pago')}</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-wide text-amber-700">{tt('Reference code (important!)', 'Código de referencia (¡importante!)')}</div>
                  <div className="font-mono text-2xl font-bold text-amber-900" data-testid="checkout-reference-code">{intent.reference_code}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(intent.reference_code); toast.success(tt('Copied', 'Copiado')); }}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-xs text-amber-700 mt-2">{tt('Put this code in the transfer "concepto" so we can match your payment.', 'Coloca este código en el "concepto" de la transferencia para que podamos identificar tu pago.')}</p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wide text-slate-500">{tt('Amount to send', 'Monto a enviar')}</Label>
              <div className="font-mono text-xl font-bold text-[#0B192C]" data-testid="checkout-amount">
                {currency === 'USD' ? fmtUSD(intent.amount_usd_cents) : fmtVES(intent.amount_ves_cents)}
              </div>
            </div>

            {method === 'bank_transfer' && (
              <div className="space-y-2">
                {config.bank_accounts.filter(b => b.currency === currency).map(b => (
                  <div key={b.id} className="space-y-2">
                    <CopyField label={tt('Bank', 'Banco')} value={b.bank_name} testId={`copy-bank-${b.id}`} />
                    <CopyField label={tt('Account number', 'Número de cuenta')} value={b.account_number} testId={`copy-acct-${b.id}`} />
                    <CopyField label={tt('Account type', 'Tipo')} value={b.account_type} />
                    <CopyField label={tt('Holder', 'Titular')} value={b.holder_name} />
                    <CopyField label="RIF / Cédula" value={b.rif} />
                  </div>
                ))}
              </div>
            )}
            {method === 'pago_movil' && config.pago_movil && (
              <div className="space-y-2">
                <CopyField label={tt('Bank', 'Banco')} value={config.pago_movil.bank_name} />
                <CopyField label="Cédula / RIF" value={config.pago_movil.id_number} />
                <CopyField label={tt('Phone', 'Teléfono')} value={config.pago_movil.phone} />
              </div>
            )}
            {method === 'zelle' && config.zelle && (
              <div className="space-y-2">
                <CopyField label="Email" value={config.zelle.email} />
                <CopyField label={tt('Name', 'Nombre')} value={config.zelle.holder_name} />
              </div>
            )}

            <Button onClick={() => setStep(3)} className="w-full bg-[#0B192C] hover:bg-[#1c2d4a] text-white" data-testid="checkout-next-upload">
              {tt('I sent the money — upload proof →', 'Ya envié el dinero — subir comprobante →')}
            </Button>
            <button onClick={() => setStep(1)} className="block text-xs text-slate-500 hover:text-[#0B192C] mx-auto">
              {tt('← Change payment method', '← Cambiar método de pago')}
            </button>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Upload proof */}
      {step === 3 && intent && (
        <Card data-testid="checkout-proof">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-heading text-lg text-[#0B192C]">{tt('Upload proof of payment', 'Sube el comprobante')}</h2>
            <p className="text-sm text-slate-600">{tt('Screenshot, photo, or PDF. Max 5 MB.', 'Captura de pantalla, foto o PDF. Máx 5 MB.')}</p>
            <label className="block">
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4AF37] file:text-[#0B192C] file:font-semibold hover:file:bg-[#C8A028]"
                data-testid="checkout-proof-input"
              />
              {proofFile && <p className="text-xs text-emerald-700 mt-1">✓ {proofFile.name} · {(proofFile.size / 1024).toFixed(0)} KB</p>}
            </label>
            <Textarea
              placeholder={tt('Optional notes (e.g. "sent from my BBVA card")', 'Notas opcionales (ej. "enviado desde mi tarjeta BBVA")')}
              value={userNotes}
              onChange={(e) => setUserNotes(e.target.value)}
              rows={2}
              data-testid="checkout-proof-notes"
            />
            <Button onClick={uploadProof} disabled={!proofFile || loading} className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]" data-testid="checkout-submit-proof">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" />{tt('Submit for review', 'Enviar a revisión')}</>}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 4 — Done */}
      {step === 4 && intent && (
        <Card className="border-emerald-300 border-2 bg-emerald-50/50" data-testid="checkout-done">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-600" />
            <h2 className="font-heading text-2xl text-[#0B192C]">{tt('Payment submitted', 'Pago enviado')}</h2>
            <p className="text-slate-600">{tt('Your payment is under review. We approve within 24 h and notify you by email.', 'Tu pago está en revisión. Aprobamos en menos de 24 h y te notificaremos por email.')}</p>
            <p className="font-mono text-sm text-slate-500">Ref: <strong>{intent.reference_code}</strong></p>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Link to="/payments"><Button variant="outline" data-testid="checkout-view-history">{tt('My payments', 'Mis pagos')}</Button></Link>
              <Link to="/agency"><Button className="bg-[#0B192C] text-white">{tt('Dashboard', 'Panel')}</Button></Link>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ============================================================
// 2) MY PAYMENTS PAGE  (/payments)
// ============================================================
export function MyPaymentsPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => {
    setLoading(true);
    axios.get(`${API}/payments/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRows(r.data.payments || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [token]);

  const cancel = async (id) => {
    if (!confirm(tt('Cancel this pending payment?', '¿Cancelar este pago pendiente?'))) return;
    try {
      await axios.post(`${API}/payments/${id}/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(tt('Cancelled', 'Cancelado'));
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="container mx-auto max-w-5xl py-6 px-4 space-y-4" data-testid="my-payments-page">
      <h1 className="font-heading text-3xl text-[#0B192C] flex items-center gap-2">
        <Wallet className="w-7 h-7 text-[#D4AF37]" />{tt('My Payments', 'Mis Pagos')}
      </h1>
      {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500" data-testid="my-payments-empty">{tt('No payments yet.', 'Aún no hay pagos.')}</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="my-payments-list">
          {rows.map(p => (
            <Card key={p.id}>
              <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0B192C]">{p.sku_name}</span>
                    <StatusPill status={p.status} lang={language} />
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    <span className="font-mono">{p.reference_code}</span> · {p.method.replace('_', ' ')} · {new Date(p.created_at).toLocaleString()}
                  </div>
                  {p.admin_notes && <div className="text-xs text-red-700 mt-1">⚠ {p.admin_notes}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-[#0B192C]">{fmtUSD(p.amount_usd_cents)}</div>
                  {['pending', 'submitted'].includes(p.status) && (
                    <Button size="sm" variant="ghost" onClick={() => cancel(p.id)} className="text-xs text-red-600 hover:bg-red-50 mt-1" data-testid={`payment-cancel-${p.id}`}>
                      <X className="w-3 h-3 mr-1" />{tt('Cancel', 'Cancelar')}
                    </Button>
                  )}
                  {p.status === 'rejected' && (
                    <Link to={`/payments/checkout?sku=${p.sku}${p.target_id ? `&target_id=${p.target_id}` : ''}`}>
                      <Button size="sm" variant="outline" className="text-xs mt-1">{tt('Retry', 'Reintentar')}</Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// 3) ADMIN PAYMENT QUEUE  (/admin/payments)
// ============================================================
export function AdminPaymentQueuePage() {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [rows, setRows] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [statusFilter, setStatusFilter] = useState('submitted');
  const [mineOnly, setMineOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [decision, setDecision] = useState({ action: '', notes: '' });

  const [accessDenied, setAccessDenied] = useState(false);

  const reload = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    if (mineOnly) params.set('assigned_to_me', 'true');
    axios.get(`${API}/payments/admin/queue?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRows(r.data.payments || []))
      .catch(e => { if (e.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/payments/admin/admins`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAdmins(r.data.admins || [])).catch(() => {});
    axios.get(`${API}/payments/admin/revenue`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setRevenue(r.data)).catch(() => {});
  }, [token]);
  useEffect(reload, [statusFilter, mineOnly, token]);

  if (accessDenied) {
    return (
      <div className="container mx-auto max-w-2xl py-10 text-center text-red-600" data-testid="admin-payments-locked">
        {tt('Admin access required', 'Acceso restringido a administradores')}
      </div>
    );
  }

  const claim = async (id) => {
    try {
      await axios.post(`${API}/payments/admin/${id}/assign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(tt('Claimed', 'Reclamado'));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const reassign = async (id, targetAdminId) => {
    try {
      await axios.post(`${API}/payments/admin/${id}/assign?target_admin_id=${targetAdminId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const unassign = async (id) => {
    try {
      await axios.post(`${API}/payments/admin/${id}/unassign`, {}, { headers: { Authorization: `Bearer ${token}` } });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const submitDecision = async (id) => {
    if (!decision.action) return;
    if (decision.action === 'reject' && !decision.notes.trim()) {
      toast.error(tt('Provide a reason', 'Indica el motivo'));
      return;
    }
    try {
      await axios.post(`${API}/payments/admin/${id}/decision`, decision, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(tt('Decision saved', 'Decisión guardada'));
      setOpenId(null);
      setDecision({ action: '', notes: '' });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  return (
    <div className="container mx-auto max-w-7xl py-6 px-4 space-y-5" data-testid="admin-payments-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-heading text-3xl text-[#0B192C] flex items-center gap-2">
          <Banknote className="w-7 h-7 text-[#D4AF37]" />{tt('Payment Queue', 'Cola de Pagos')}
        </h1>
        {user?.role === 'superadmin' && (
          <Link to="/admin/payment-config">
            <Button variant="outline" className="gap-2" data-testid="admin-payments-config-btn">
              <Settings className="w-4 h-4" />{tt('Payment Config', 'Configuración')}
            </Button>
          </Link>
        )}
      </div>

      {/* Revenue stats */}
      {revenue && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-revenue-strip">
          <RevenueStat label="MRR" value={fmtUSD(revenue.mrr_usd_cents)} icon={TrendingUp} accent="emerald" testId="revenue-mrr" />
          <RevenueStat label={tt('This month', 'Este mes')} value={fmtUSD(revenue.this_month.usd_cents)} sub={`${revenue.this_month.count} ${tt('payments', 'pagos')}`} icon={DollarSign} testId="revenue-month" />
          <RevenueStat label={tt('Last 30 d', 'Últimos 30 d')} value={fmtUSD(revenue.last_30_days.usd_cents)} sub={`${revenue.last_30_days.count} ${tt('payments', 'pagos')}`} icon={Clock} testId="revenue-30d" />
          <RevenueStat label={tt('Pending', 'Pendientes')} value={fmtUSD(revenue.pending.usd_cents)} sub={`${revenue.pending.count} ${tt('to review', 'por revisar')}`} icon={AlertTriangle} accent="amber" testId="revenue-pending" />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap bg-slate-50 rounded-lg p-3">
        {['submitted', 'pending', 'approved', 'rejected', 'expired', 'cancelled'].map(s => (
          <button key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1.5 rounded-full transition ${statusFilter === s ? 'bg-[#0B192C] text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}
            data-testid={`admin-filter-${s}`}>
            {STATUS_STYLES[s]?.label[language] || s}
          </button>
        ))}
        <label className="flex items-center gap-2 ml-auto text-xs text-slate-600">
          <Switch checked={mineOnly} onCheckedChange={setMineOnly} data-testid="admin-mine-only-toggle" />
          {tt('Assigned to me', 'Asignados a mí')}
        </label>
        <Button size="sm" variant="ghost" onClick={reload} data-testid="admin-refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
      </div>

      {/* Queue */}
      {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500" data-testid="admin-queue-empty">{tt('Nothing here.', 'Nada por aquí.')}</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="admin-queue-list">
          {rows.map(p => (
            <Card key={p.id} data-testid={`admin-queue-row-${p.id}`}>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#0B192C]">{p.sku_name}</span>
                      <StatusPill status={p.status} lang={language} />
                      <span className="font-mono text-xs text-slate-500">{p.reference_code}</span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {p.user_email} · {p.method.replace('_', ' ')} · {new Date(p.created_at).toLocaleString()}
                    </div>
                    {p.user_notes && <div className="text-xs text-slate-700 mt-1 italic">"{p.user_notes}"</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-lg text-[#0B192C]">{fmtUSD(p.amount_usd_cents)}</div>
                    {p.currency === 'VES' && <div className="text-[10px] text-slate-500">≈ {fmtVES(p.amount_ves_cents)}</div>}
                  </div>
                </div>

                {/* Assignment row */}
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  <span className="text-slate-500">{tt('Assigned:', 'Asignado:')}</span>
                  {p.assigned_admin_email ? (
                    <Badge className="bg-blue-100 text-blue-700 gap-1">
                      <Users className="w-3 h-3" />{p.assigned_admin_email}
                      {(p.assigned_admin_id === user?.id || user?.role === 'superadmin') && (
                        <button onClick={() => unassign(p.id)} className="ml-1 hover:text-red-600"><X className="w-3 h-3" /></button>
                      )}
                    </Badge>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => claim(p.id)} className="text-xs h-6 px-2" data-testid={`admin-claim-${p.id}`}>
                      {tt('Claim', 'Reclamar')}
                    </Button>
                  )}
                  {user?.role === 'superadmin' && (
                    <select onChange={(e) => e.target.value && reassign(p.id, e.target.value)}
                      value=""
                      className="text-xs border border-slate-200 rounded px-2 py-1 ml-2"
                      data-testid={`admin-reassign-${p.id}`}>
                      <option value="">{tt('Reassign to…', 'Reasignar a…')}</option>
                      {admins.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
                    </select>
                  )}
                </div>

                {/* Actions */}
                {p.proof_image_url && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <a href={p.proof_image_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`admin-view-proof-${p.id}`}>
                      <Eye className="w-3 h-3" />{tt('View proof', 'Ver comprobante')}
                    </a>
                    {p.status === 'submitted' && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7"
                          onClick={() => { setOpenId(p.id); setDecision({ action: 'approve', notes: '' }); }}
                          data-testid={`admin-approve-btn-${p.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{tt('Approve', 'Aprobar')}
                        </Button>
                        <Button size="sm" variant="outline" className="text-xs h-7 border-red-300 text-red-700"
                          onClick={() => { setOpenId(p.id); setDecision({ action: 'reject', notes: '' }); }}
                          data-testid={`admin-reject-btn-${p.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" />{tt('Reject', 'Rechazar')}
                        </Button>
                      </>
                    )}
                  </div>
                )}

                {/* Decision form */}
                {openId === p.id && (
                  <div className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`admin-decision-form-${p.id}`}>
                    <Label className="text-xs">{decision.action === 'approve' ? tt('Optional approval notes', 'Notas opcionales (aprobar)') : tt('Rejection reason (required)', 'Motivo de rechazo (requerido)')}</Label>
                    <Textarea value={decision.notes} onChange={(e) => setDecision({ ...decision, notes: e.target.value })} rows={2} data-testid={`admin-decision-notes-${p.id}`} />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => submitDecision(p.id)} className={decision.action === 'approve' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'} data-testid={`admin-confirm-${decision.action}-${p.id}`}>
                        {tt('Confirm', 'Confirmar')} {decision.action}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setOpenId(null)}>{tt('Cancel', 'Cancelar')}</Button>
                    </div>
                  </div>
                )}

                {p.admin_notes && <div className="text-xs text-slate-600 bg-slate-50 rounded p-2">📝 {p.admin_notes}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function RevenueStat({ label, value, sub, icon: Icon, accent = 'slate', testId }) {
  const accents = { slate: 'text-slate-700', emerald: 'text-emerald-600', amber: 'text-amber-600' };
  return (
    <Card data-testid={testId}><CardContent className="p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
        <Icon className={`w-4 h-4 ${accents[accent]}`} />
      </div>
      <p className={`font-mono text-2xl font-bold ${accents[accent]}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </CardContent></Card>
  );
}

// ============================================================
// 4) SUPER ADMIN CONFIG  (/admin/payment-config)
// ============================================================
export function PaymentAdminConfigPage() {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const tt = (en, es) => (language === 'es' ? es : en);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/payments/config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCfg(r.data))
      .catch(() => toast.error(tt('Super admin only', 'Solo super admin')));
  }, [token]);

  if (user?.role !== 'superadmin') {
    return <div className="container mx-auto max-w-2xl py-10 text-center text-red-600" data-testid="config-locked">{tt('Super admin only', 'Solo super admin')}</div>;
  }
  if (!cfg) return <Loader2 className="w-8 h-8 animate-spin mx-auto mt-10" />;

  const updateSku = (k, patch) => setCfg({ ...cfg, skus: { ...cfg.skus, [k]: { ...cfg.skus[k], ...patch } } });
  const updateBank = (id, patch) => setCfg({ ...cfg, bank_accounts: cfg.bank_accounts.map(b => b.id === id ? { ...b, ...patch } : b) });
  const addBank = () => setCfg({ ...cfg, bank_accounts: [...cfg.bank_accounts, { id: `ba-${Date.now()}`, bank_name: '', account_number: '', account_type: 'Corriente', holder_name: '', rif: '', currency: 'USD', active: true }] });
  const removeBank = (id) => setCfg({ ...cfg, bank_accounts: cfg.bank_accounts.filter(b => b.id !== id) });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/payments/config`, {
        skus: cfg.skus,
        grace_period_days: cfg.grace_period_days,
        auto_expire_pending_hours: cfg.auto_expire_pending_hours,
        reminder_days_before_expiry: cfg.reminder_days_before_expiry,
        bank_accounts: cfg.bank_accounts,
        pago_movil: cfg.pago_movil,
        zelle: cfg.zelle,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCfg(data);
      toast.success(tt('Saved', 'Guardado'));
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setSaving(false); }
  };

  return (
    <div className="container mx-auto max-w-5xl py-6 px-4 space-y-5" data-testid="payment-config-page">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-[#0B192C] flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" />{tt('Back', 'Volver')}
      </button>
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl text-[#0B192C] flex items-center gap-2">
          <Settings className="w-7 h-7 text-[#D4AF37]" />{tt('Payment Configuration', 'Configuración de Pagos')}
        </h1>
        <Button onClick={save} disabled={saving} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-2" data-testid="config-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {tt('Save changes', 'Guardar')}
        </Button>
      </div>

      <Tabs defaultValue="skus">
        <TabsList>
          <TabsTrigger value="skus" data-testid="tab-skus">{tt('Plans & SKUs', 'Planes')}</TabsTrigger>
          <TabsTrigger value="banks" data-testid="tab-banks">{tt('Bank Accounts', 'Cuentas')}</TabsTrigger>
          <TabsTrigger value="alt" data-testid="tab-alt">Pago Móvil / Zelle</TabsTrigger>
          <TabsTrigger value="policy" data-testid="tab-policy">{tt('Policy', 'Política')}</TabsTrigger>
        </TabsList>

        <TabsContent value="skus" className="space-y-3">
          {Object.entries(cfg.skus).map(([k, v]) => (
            <Card key={k} data-testid={`sku-card-${k}`}>
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 flex items-center justify-between">
                  <Badge className="bg-slate-100 text-slate-700 font-mono text-xs">{k}</Badge>
                  <label className="flex items-center gap-2 text-xs"><Switch checked={v.active} onCheckedChange={(b) => updateSku(k, { active: b })} data-testid={`sku-${k}-active`} /> {tt('Active', 'Activo')}</label>
                </div>
                <div><Label>{tt('Name (EN)', 'Nombre (EN)')}</Label><Input value={v.name_en} onChange={(e) => updateSku(k, { name_en: e.target.value })} data-testid={`sku-${k}-name-en`} /></div>
                <div><Label>{tt('Name (ES)', 'Nombre (ES)')}</Label><Input value={v.name_es} onChange={(e) => updateSku(k, { name_es: e.target.value })} data-testid={`sku-${k}-name-es`} /></div>
                <div><Label>{tt('Price (USD)', 'Precio (USD)')}</Label><Input type="number" min="0" step="0.01" value={v.price_usd_cents / 100} onChange={(e) => updateSku(k, { price_usd_cents: Math.round(parseFloat(e.target.value || '0') * 100) })} data-testid={`sku-${k}-price`} /></div>
                <div><Label>{tt('Duration (days)', 'Duración (días)')}</Label><Input type="number" min="0" value={v.duration_days} onChange={(e) => updateSku(k, { duration_days: parseInt(e.target.value || '0') })} data-testid={`sku-${k}-duration`} /></div>
                {(k === 'agent_independent' || k === 'agent_pro') && (
                  <div className="md:col-span-2">
                    <Label className="flex items-center gap-2">
                      {tt('Listing limit', 'Límite de propiedades')}
                      <span className="text-[10px] text-slate-400 font-normal">
                        {tt('Empty = unlimited', 'Vacío = ilimitado')}
                      </span>
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      placeholder={tt('Unlimited', 'Ilimitado')}
                      value={v.listing_limit == null ? '' : v.listing_limit}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === '') {
                          updateSku(k, { listing_limit: null });
                        } else {
                          const n = parseInt(raw, 10);
                          updateSku(k, { listing_limit: Number.isFinite(n) && n >= 0 ? n : null });
                        }
                      }}
                      data-testid={`sku-${k}-listing-limit`}
                    />
                    <p className="text-[10px] text-slate-500 mt-1">
                      {tt(
                        'How many active listings (approved + pending) an agent on this plan can have.',
                        'Cuántas propiedades activas (aprobadas + pendientes) puede tener un agente en este plan.'
                      )}
                    </p>
                  </div>
                )}
                <div className="md:col-span-2"><Label>{tt('Description (ES)', 'Descripción (ES)')}</Label><Textarea rows={2} value={v.description_es} onChange={(e) => updateSku(k, { description_es: e.target.value })} data-testid={`sku-${k}-desc-es`} /></div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="banks" className="space-y-3">
          {cfg.bank_accounts.map(b => (
            <Card key={b.id} data-testid={`bank-${b.id}`}>
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2 flex items-center justify-between">
                  <Badge className="bg-slate-100 font-mono text-xs">{b.currency}</Badge>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-xs"><Switch checked={b.active} onCheckedChange={(v) => updateBank(b.id, { active: v })} /> Active</label>
                    <Button size="sm" variant="ghost" onClick={() => removeBank(b.id)} className="text-red-600"><X className="w-4 h-4" /></Button>
                  </div>
                </div>
                <div><Label>{tt('Bank', 'Banco')}</Label><Input value={b.bank_name} onChange={(e) => updateBank(b.id, { bank_name: e.target.value })} /></div>
                <div><Label>{tt('Account #', 'Cuenta #')}</Label><Input value={b.account_number} onChange={(e) => updateBank(b.id, { account_number: e.target.value })} /></div>
                <div><Label>{tt('Type', 'Tipo')}</Label><Input value={b.account_type} onChange={(e) => updateBank(b.id, { account_type: e.target.value })} /></div>
                <div><Label>{tt('Holder', 'Titular')}</Label><Input value={b.holder_name} onChange={(e) => updateBank(b.id, { holder_name: e.target.value })} /></div>
                <div><Label>RIF / Cédula</Label><Input value={b.rif} onChange={(e) => updateBank(b.id, { rif: e.target.value })} /></div>
                <div><Label>{tt('Currency', 'Moneda')}</Label>
                  <select value={b.currency} onChange={(e) => updateBank(b.id, { currency: e.target.value })} className="w-full border border-slate-200 rounded px-3 py-2 text-sm">
                    <option>USD</option><option>VES</option>
                  </select>
                </div>
              </CardContent>
            </Card>
          ))}
          <Button onClick={addBank} variant="outline" className="w-full" data-testid="add-bank-btn">+ {tt('Add bank account', 'Agregar cuenta')}</Button>
        </TabsContent>

        <TabsContent value="alt" className="space-y-4">
          <Card><CardContent className="p-4 space-y-3" data-testid="config-pago-movil">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><Smartphone className="w-4 h-4 text-[#D4AF37]" />Pago Móvil</h3>
              <label className="flex items-center gap-2 text-xs"><Switch checked={cfg.pago_movil?.active} onCheckedChange={(v) => setCfg({ ...cfg, pago_movil: { ...cfg.pago_movil, active: v } })} /> Active</label>
            </div>
            <Input placeholder={tt('Bank', 'Banco')} value={cfg.pago_movil?.bank_name || ''} onChange={(e) => setCfg({ ...cfg, pago_movil: { ...cfg.pago_movil, bank_name: e.target.value } })} />
            <Input placeholder="Cédula / RIF" value={cfg.pago_movil?.id_number || ''} onChange={(e) => setCfg({ ...cfg, pago_movil: { ...cfg.pago_movil, id_number: e.target.value } })} />
            <Input placeholder={tt('Phone', 'Teléfono')} value={cfg.pago_movil?.phone || ''} onChange={(e) => setCfg({ ...cfg, pago_movil: { ...cfg.pago_movil, phone: e.target.value } })} />
          </CardContent></Card>
          <Card><CardContent className="p-4 space-y-3" data-testid="config-zelle">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#D4AF37]" />Zelle</h3>
              <label className="flex items-center gap-2 text-xs"><Switch checked={cfg.zelle?.active} onCheckedChange={(v) => setCfg({ ...cfg, zelle: { ...cfg.zelle, active: v } })} /> Active</label>
            </div>
            <Input placeholder="Email" value={cfg.zelle?.email || ''} onChange={(e) => setCfg({ ...cfg, zelle: { ...cfg.zelle, email: e.target.value } })} />
            <Input placeholder={tt('Holder name', 'Titular')} value={cfg.zelle?.holder_name || ''} onChange={(e) => setCfg({ ...cfg, zelle: { ...cfg.zelle, holder_name: e.target.value } })} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="policy" className="space-y-3">
          <Card><CardContent className="p-4 space-y-3" data-testid="config-policy">
            <div><Label>{tt('Grace period after subscription expiry (days)', 'Período de gracia tras expiración (días)')}</Label><Input type="number" min="0" max="365" value={cfg.grace_period_days} onChange={(e) => setCfg({ ...cfg, grace_period_days: parseInt(e.target.value || '0') })} data-testid="config-grace-period" /></div>
            <div><Label>{tt('Auto-expire pending payments after (hours)', 'Auto-expirar pagos pendientes tras (horas)')}</Label><Input type="number" min="1" max="720" value={cfg.auto_expire_pending_hours} onChange={(e) => setCfg({ ...cfg, auto_expire_pending_hours: parseInt(e.target.value || '24') })} data-testid="config-expire-hours" /></div>
            <div><Label>{tt('Send renewal reminder N days before expiry', 'Enviar recordatorio de renovación N días antes')}</Label><Input type="number" min="0" max="60" value={cfg.reminder_days_before_expiry} onChange={(e) => setCfg({ ...cfg, reminder_days_before_expiry: parseInt(e.target.value || '3') })} data-testid="config-reminder-days" /></div>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
